import {BoxTraverser} from '@mintdata-wsp/common/controllers/surface/BoxVisitor';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {LayoutRecalc, LayoutUtils} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/LayoutUtils';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {BoxReparentingState, MouseOverRef} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {BoxHostId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {Position} from '@mintdata/mint-kit/api/models/Position';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {ANumber} from '@mintdata/mint-kit/api/values/ANumber';
import {action} from 'mobx';

export enum BoxReparentingDisabledOn
{
    Drop = 'Drop',
    ShortcutUp = 'ShortcutUp',
    Esc = 'Esc',
}

export class SurfaceBoxReparentingController
{
    public isBoxReparentingEnabled():boolean
    {
        return this._getState().isEnabled;
    }

    @action
    public enableBoxReparenting(isForceUpdateState:boolean = false):void
    {
        const {surfaceArea, surfaceSelection, surfaceMoving} = controllers.ui;
        const state = this._getState();

        if( !isForceUpdateState && state.isEnabled )
            return;

        const selectedBoxes = surfaceSelection.getOrderedSelectedBoxes();

        if( selectedBoxes.length === 0 || selectedBoxes.find((box:Box) => box.isViewport) )
            return;

        // todo: support reparenting of parent directed boxes
        if( selectedBoxes.find((box:Box) => !box.componentDesc.isSelfDirected) )
            return;

        state.isEnabled = true;

        selectedBoxes.forEach((box:Box) =>
        {
            state.initialParents.set(box.id, box.parent);
            state.initialHosts.set(box.id, box.hostId);
            state.initialPositions.set(box.id, Position.fromPixels(box.bounds.x, box.bounds.y));
        });

        const repeatedBoxOffsetOnEnable:Point = this._computeRepeatedBoxOffsetOnEnable();
        const repeatedBoxOffsetOnDisable:Point = this._computeRepeatedBoxOffsetOnDisable();

        state.repeatedBoxOffsetOnEnable = repeatedBoxOffsetOnEnable;
        state.repeatedBoxOffsetOnDisable = repeatedBoxOffsetOnDisable;

        this.adjustReparentingBoxesPosition(selectedBoxes, null, null, null, repeatedBoxOffsetOnEnable);

        selectedBoxes.forEach((box:Box) => surfaceArea.moveIntoBox(box, null, null));
        surfaceArea.setMouseOverRef(null);

        const bounds = surfaceArea.getBoxesBounds(selectedBoxes);

        surfaceSelection.setSelectionBounds(bounds);

        surfaceMoving.setBoxDrag(bounds.x, bounds.y, 0, 0);

        requestAnimationFrame(() => surfaceArea.focusSurfaceArea());
    }

    @action
    public disableBoxReparenting(disabledOn:BoxReparentingDisabledOn = BoxReparentingDisabledOn.Drop):void
    {
        const {surfaceArea, surfaceSelection, surfaceMoving, surfacePagesPanel, undoRedo} = controllers.ui;
        const state = this._getState();

        if( !state.isEnabled )
            return;

        state.isEnabled = false;

        const selectedBoxes = surfaceSelection.getOrderedSelectedBoxes();

        switch( disabledOn )
        {
            case BoxReparentingDisabledOn.Drop:
            {
                const mouseOverRef:MouseOverRef = surfaceArea.getMouseOverRef();
                let newParent:Box = mouseOverRef ? mouseOverRef.box : null;
                const newParentRepeater:Repeater = mouseOverRef ? mouseOverRef.repeater : null;
                const newParentHostId:BoxHostId = mouseOverRef ? mouseOverRef.hostId : null;

                this.adjustReparentingBoxesPosition(
                    selectedBoxes,
                    newParent,
                    newParentRepeater,
                    newParentHostId,
                    state.repeatedBoxOffsetOnDisable
                );

                if( selectedBoxes.some(box => newParent && box.id === newParent.id) )
                    newParent = null;

                selectedBoxes.forEach((box:Box) =>
                {
                    surfaceArea.moveIntoBox(box, newParent, newParentHostId);
                    surfacePagesPanel.openOutlineItemParents(box);
                });

                state.initialParents.clear();
                state.initialHosts.clear();
                state.initialPositions.clear();

                // TODO: Temporary solution, need to remove when reparenting issues will be fixed
                setTimeout(undoRedo.clear);

                break;
            }

            case BoxReparentingDisabledOn.ShortcutUp:
            {
                selectedBoxes.forEach((box:Box) =>
                {
                    const newParent:Box = state.initialParents.get(box.id);
                    const newHostId:BoxHostId = state.initialHosts.get(box.id);
                    const newPosition:Position = state.initialPositions.get(box.id);

                    const {boxDragDeltaX, boxDragDeltaY} = surfaceMoving.getBoxDrag();

                    const newPositionX:number = newPosition.left.value.value + boxDragDeltaX;
                    const newPositionY:number = newPosition.top.value.value + boxDragDeltaY;

                    const {layoutLeftProp, layoutTopProp} = box.componentDesc;

                    layoutLeftProp.setInnerValue(new Units(ANumber.fromNumber(newPositionX), newPosition.left.unitsType));
                    layoutTopProp.setInnerValue(new Units(ANumber.fromNumber(newPositionY), newPosition.top.unitsType));

                    surfaceArea.moveIntoBox(box, newParent, newHostId);
                });

                break;
            }

            case BoxReparentingDisabledOn.Esc:
            {
                selectedBoxes.forEach((box:Box) =>
                {
                    const newParent:Box = state.initialParents.get(box.id);
                    const newHostId:BoxHostId = state.initialHosts.get(box.id);
                    const newPosition:Position = state.initialPositions.get(box.id);

                    const {layoutLeftProp, layoutTopProp} = box.componentDesc;

                    layoutLeftProp.setInnerValue(newPosition.left);
                    layoutTopProp.setInnerValue(newPosition.top);

                    surfaceArea.moveIntoBox(box, newParent, newHostId);
                });

                state.initialParents.clear();
                state.initialPositions.clear();

                break;
            }
        }

        const bounds = surfaceArea.getBoxesBounds(selectedBoxes);

        surfaceSelection.setSelectionBounds(bounds);

        surfaceMoving.setBoxDrag(bounds.x, bounds.y, 0, 0);
    }

    @action
    public adjustReparentingBoxesPosition(boxes:Box[],
                                          newParent:Box = null,
                                          newParentRepeater:Repeater = null,
                                          newParentHostId:BoxHostId = null,
                                          extraOffset:Point = Point.zero()):void
    {
        const {surfaceArea} = controllers.ui;

        let offsetX:number = 0;
        let offsetY:number = 0;

        let hostClientRect:ClientRect = null;

        if( newParent )
        {
            const hostRef:HTMLElement = newParentRepeater
                ? surfaceArea.getRepeatedBoxHostRef(newParent, newParentRepeater, newParentHostId)
                : surfaceArea.getBoxHostRef(newParent, newParentHostId);

            const parentRef:HTMLElement = newParentRepeater
                ? surfaceArea.getRepeatedBoxRef(newParent, newParentRepeater)
                : surfaceArea.getBoxRef(newParent);

            if( hostRef )
            {
                hostClientRect = LayoutUtils.getBoundingClientRect(hostRef);
                const hostRefPoint:Point = surfaceArea.getBoxRefAbsolutePoint(hostRef);

                offsetX = hostRefPoint.x;
                offsetY = hostRefPoint.y;
            }
            else if( parentRef )
            {
                hostClientRect = LayoutUtils.getBoundingClientRect(parentRef);
                const parentRefPoint:Point = surfaceArea.getBoxRefAbsolutePoint(parentRef);

                offsetX = parentRefPoint.x;
                offsetY = parentRefPoint.y;
            }
            else
            {
                offsetX = newParent.absoluteBounds.x;
                offsetY = newParent.absoluteBounds.y;
            }
        }

        boxes.forEach((box:Box) =>
        {
            const {layoutLeft, layoutTop, layoutLeftProp, layoutTopProp} = box.componentDesc;

            let boxPositionX:number = box.absoluteBounds.x;
            let boxPositionY:number = box.absoluteBounds.y;

            const boxRef:HTMLElement = surfaceArea.getBoxRef(box);

            if( box.parent != null && boxRef )
            {
                const boxRefPoint:Point = surfaceArea.getBoxRefAbsolutePoint(boxRef);

                boxPositionX = boxRefPoint.x;
                boxPositionY = boxRefPoint.y;
            }

            const newPosX = Math.round(boxPositionX - offsetX + extraOffset.x);
            const newPosY = Math.round(boxPositionY - offsetY + extraOffset.y);

            const newLeftValue = new Units(ANumber.fromNumber(newPosX), layoutLeft.unitsType);
            const newTopValue = new Units(ANumber.fromNumber(newPosY), layoutTop.unitsType);

            layoutLeftProp.setInnerValue(newLeftValue);
            layoutTopProp.setInnerValue(newTopValue);

            LayoutUtils.recalcDimensions({
                componentDesc: box.componentDesc,
                hostClientRect,
                layoutRecalc: LayoutRecalc.MOVED_HORIZONTAL
            });

            LayoutUtils.recalcDimensions({
                componentDesc: box.componentDesc,
                hostClientRect,
                layoutRecalc: LayoutRecalc.MOVED_VERTICAL
            });
        });
    }

    public isReparentingBox(box:Box):boolean
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        const state = this._getState();

        if( !this.isBoxReparentingEnabled() )
            return false;

        return state.initialPositions.has(box.id) || activeUsers.otherUserBoxReparentingState.has(box.id);
    }

    public isChildOfReparentingBox(box:Box):boolean
    {
        const {activeUsers} = rootState.ui.visualDesigner;

        const state = this._getState();

        if( !this.isBoxReparentingEnabled() )
            return false;

        let isChildOfReparentingBox:boolean = false;

        BoxTraverser.visit(box, {
            onBox: (currentBox:Box) =>
            {
                if( state.initialPositions.has(currentBox.id) || activeUsers.otherUserBoxReparentingState.has(box.id) )
                    isChildOfReparentingBox = true;
            }
        }, true);

        return isChildOfReparentingBox;
    }

    public isDropIntoBoxAllowed(box:Box):boolean
    {
        const {surfaceArea, surfaceGlass} = controllers.ui;

        if( this.isReparentingBox(box) || this.isChildOfReparentingBox(box) )
            return false;

        const isBoxContainer:boolean = surfaceArea.isBoxContainer(box);
        const isBoxOnCurrentLayerPage:boolean = surfaceArea.isBoxOnCurrentLayerPage(box);
        const isViewport:boolean = box.isViewport;
        const isDraggingComponentFromPanel:boolean = surfaceGlass.isDraggingComponent();
        const isReparentingComponentFromPanel:boolean = surfaceGlass.isReparentingComponent();

        if( this.isBoxReparentingEnabled() || isReparentingComponentFromPanel )
            return isBoxOnCurrentLayerPage && isBoxContainer;

        if( isDraggingComponentFromPanel && !isReparentingComponentFromPanel )
            return isBoxOnCurrentLayerPage && isViewport;

        return false;
    }

    private _computeRepeatedBoxOffsetOnEnable():Point
    {
        const {surfaceArea} = controllers.ui;
        const {zoom} = rootState.ui.visualDesigner.surfaceEditor.area;

        const mouseOverRef:MouseOverRef = surfaceArea.getMouseOverRef();

        if( mouseOverRef )
        {
            const repeatedBoxRef:HTMLElement = surfaceArea.getRepeatedBoxRef(mouseOverRef.box, mouseOverRef.repeater);
            const boxRef:HTMLElement = surfaceArea.getBoxRef(mouseOverRef.box);

            if( repeatedBoxRef && boxRef )
            {
                const repeatedBoxBounds:ClientRect = repeatedBoxRef.getBoundingClientRect();
                const boxBounds:ClientRect = boxRef.getBoundingClientRect();

                return Point.from(
                    (repeatedBoxBounds.left - boxBounds.left) / zoom,
                    (repeatedBoxBounds.top - boxBounds.top) / zoom
                );
            }
        }

        return Point.zero();
    }

    private _computeRepeatedBoxOffsetOnDisable():Point
    {
        const {surfaceArea} = controllers.ui;
        const {zoom} = rootState.ui.visualDesigner.surfaceEditor.area;

        const mouseOverRef:MouseOverRef = surfaceArea.getMouseOverRef();

        if( mouseOverRef )
        {
            const repeatedBoxRef:HTMLElement = surfaceArea.getRepeatedBoxRef(mouseOverRef.box, mouseOverRef.repeater);
            const firstOfLastLevelRepeatedBoxRef:HTMLElement = surfaceArea.getFirstOfLastLevelRepeatedBoxRef(mouseOverRef.box, mouseOverRef.repeater);

            if( repeatedBoxRef && firstOfLastLevelRepeatedBoxRef )
            {
                const repeatedBoxBounds:ClientRect = repeatedBoxRef.getBoundingClientRect();
                const firstOfLastLevelRepeatedBoxBounds:ClientRect = firstOfLastLevelRepeatedBoxRef.getBoundingClientRect();

                return Point.from(
                    (firstOfLastLevelRepeatedBoxBounds.left - repeatedBoxBounds.left) / zoom,
                    (firstOfLastLevelRepeatedBoxBounds.top - repeatedBoxBounds.top) / zoom
                );
            }
        }

        return Point.zero();
    }

    private _getState():BoxReparentingState
    {
        return rootState.ui.visualDesigner.surfaceEditor.area.boxReparentingState;
    }
}
